/**
 * The end of Foundations, and of each preview: which discipline next.
 *
 * Software is the full course. Power and Energy and Civil are five-task
 * previews (`/power`, `/civil`); their full courses are not built, and the
 * "I want the full course" button is a vote. The votes are the demand signal
 * that decides which is built first (docs/foundations.md §4). A vote is one
 * closed word in the funnel (`discipline-vote`), never a name or an address,
 * which is why there is no waiting-list form here.
 *
 * Drawn whether or not the steps are finished: somebody who came for the
 * software course should be one click from it, not seven tasks.
 */
import { useState } from 'react';
import { Button, LinkButton, Tag } from '../../ui';
import { useStrings } from '../../state/useLocale';
import { track } from '../../lib/events';
import { loadVotes, saveVotes, type Discipline } from '../../lib/foundations';
import { TRACK_PATHS, type TrackId } from '../../lib/tracks';

/** The first level of the software course. */
const SOFTWARE_START = '/?mission=l00-hello-terminal';

export function Disciplines({ here }: { here?: TrackId }) {
  const d = useStrings().foundations.disciplines;
  const [votes, setVotes] = useState<Set<Discipline>>(() => loadVotes());

  const vote = (which: Discipline) => {
    if (votes.has(which)) return;
    const next = new Set(votes);
    next.add(which);
    setVotes(next);
    saveVotes(next);
    track('discipline-vote', { detail: which });
  };

  return (
    <section className="fdisc" id="disciplines" aria-labelledby="disciplines-title">
      <p className="label">{d.label}</p>
      <h2 className="fdisc__title" id="disciplines-title">
        {d.title}
      </h2>
      <p className="fdisc__lede">{d.lede}</p>
      <div className="fdisc__grid">
        <article className="fdisc__card fdisc__card--open">
          <h3>{d.software.name}</h3>
          <p>{d.software.what}</p>
          <LinkButton variant="solid" href={SOFTWARE_START} onClick={() => track('discipline-vote', { detail: 'software' })}>
            {d.software.cta}
          </LinkButton>
        </article>
        {(['power', 'civil'] as const).map((which) => (
          <article key={which} className={here === which ? 'fdisc__card is-here' : 'fdisc__card'}>
            <h3>
              {d[which].name} <Tag tone="off">{d.preview}</Tag>
            </h3>
            <p>{d[which].what}</p>
            {here === which ? (
              <p className="fdisc__here">{d.here}</p>
            ) : (
              <LinkButton variant="solid" href={TRACK_PATHS[which]}>
                {d.tryPreview}
              </LinkButton>
            )}
            {/* The power course's first levels (docs/grid.md), English only for now. */}
            {which === 'power' && <LinkButton href={TRACK_PATHS.grid}>{d.gridCourse}</LinkButton>}
            {votes.has(which) ? (
              <p className="fdisc__voted" role="status">
                {d.voted}
              </p>
            ) : (
              <Button onClick={() => vote(which)}>{d.vote}</Button>
            )}
          </article>
        ))}
      </div>
      {here && (
        <p className="fdisc__foundations">
          <a href={TRACK_PATHS.foundations}>{d.backToFoundations}</a>
        </p>
      )}
    </section>
  );
}
